import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { StatusBadge } from '../components/Badges';
import WhatsAppButton from '../components/WhatsAppButton';
import { CalendarDays, Search, RefreshCw, Clock, User } from 'lucide-react';

function todayISO() { return new Date().toISOString().slice(0,10); }

export default function ReschedulePage() {
  const { showToast } = useToast();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [dateFilter, setDateFilter]     = useState('upcoming'); // 'upcoming'|'all'|'today'
  const [rescheduleId, setRescheduleId] = useState(null);
  const [newSlot, setNewSlot]           = useState('');
  const [newDate, setNewDate]           = useState('');
  const [saving, setSaving]             = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const today = todayISO();
      // Load appointments from calendar API — all dates
      const res = await api.get('/patients/calendar', { params: { date: 'all' } });
      let appts = res.data.appointments || [];
      // Filter
      if (dateFilter === 'today')    appts = appts.filter(a => a.appointmentDate === today);
      if (dateFilter === 'upcoming') appts = appts.filter(a => a.appointmentDate >= today);
      // Search
      if (search.trim()) {
        const q = search.toLowerCase();
        appts = appts.filter(a =>
          (a.name||'').toLowerCase().includes(q) ||
          (a.patientUID||'').toLowerCase().includes(q) ||
          (a.phone||'').toLowerCase().includes(q)
        );
      }
      appts.sort((a,b) => {
        if (a.appointmentDate !== b.appointmentDate) return a.appointmentDate > b.appointmentDate ? 1 : -1;
        return (a.appointmentTime||'') > (b.appointmentTime||'') ? 1 : -1;
      });
      setAppointments(appts);
    } catch { showToast('Could not load appointments.', 'error'); }
    finally { setLoading(false); }
  }, [dateFilter, search, showToast]);

  useEffect(() => { load(); }, [load]);

  async function handleReschedule(p) {
    if (!newDate && !newSlot) { showToast('Enter a new date or slot.', 'error'); return; }
    setSaving(true);
    try {
      const updates = {};
      if (newDate) updates.appointmentDate = newDate;
      if (newSlot) updates.appointmentSlot = newSlot;
      await api.put(`/patients/${p._id}`, updates);
      showToast('Appointment rescheduled.', 'success');
      setRescheduleId(null); setNewSlot(''); setNewDate('');
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not reschedule.', 'error');
    } finally { setSaving(false); }
  }

  const today = todayISO();

  return (
    <AppLayout title="Reschedule" subtitle="Manage and reschedule patient appointments">

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:18, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ display:'flex', gap:0, border:'1px solid var(--color-border)', borderRadius:8, overflow:'hidden' }}>
          {[['today','Today'],['upcoming','Upcoming'],['all','All']].map(([key,label]) => (
            <button key={key} className={`btn ${dateFilter===key?'btn-primary':'btn-ghost'} btn-sm`}
              style={{ borderRadius:0 }} onClick={() => setDateFilter(key)}>
              {label}
            </button>
          ))}
        </div>
        <div style={{ position:'relative', flex:'1 1 200px' }}>
          <Search size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--color-ink-faint)' }}/>
          <input className="input" style={{ paddingLeft:32 }} placeholder="Search by name, ID, phone…" value={search} onChange={e => setSearch(e.target.value)}/>
        </div>
        <button className="btn btn-outline btn-sm" onClick={load}><RefreshCw size={13}/> Refresh</button>
      </div>

      {/* Stats row */}
      <div style={{ display:'flex', gap:16, marginBottom:18, flexWrap:'wrap' }}>
        {[
          ['Today', appointments.filter(a => a.appointmentDate === today).length, 'var(--color-primary)'],
          ['Upcoming', appointments.filter(a => a.appointmentDate > today).length, 'var(--color-info)'],
          ['Total Shown', appointments.length, 'var(--color-ink-soft)'],
        ].map(([label, count, color]) => (
          <div key={label} className="card" style={{ padding:'10px 16px', display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ fontWeight:800, fontSize:20, color }}>{count}</div>
            <div style={{ fontSize:12, color:'var(--color-ink-faint)' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Appointment list */}
      {loading ? (
        <div style={{ display:'grid', gap:10 }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height:80, borderRadius:10 }}/>)}
        </div>
      ) : appointments.length === 0 ? (
        <div className="card" style={{ padding:48, textAlign:'center', color:'var(--color-ink-faint)' }}>
          <CalendarDays size={36} style={{ marginBottom:12, opacity:0.4 }}/>
          <p style={{ margin:0 }}>No appointments found.</p>
        </div>
      ) : (
        <div style={{ display:'grid', gap:10 }}>
          {appointments.map(p => {
            const isEditing = rescheduleId === p._id;
            const isOverdue = p.appointmentDate && p.appointmentDate < today && p.status !== 'completed' && p.status !== 'cancelled';
            return (
              <div key={p._id} className="card" style={{ padding:16, borderLeft: isOverdue ? '3px solid var(--color-danger)' : p.appointmentDate === today ? '3px solid var(--color-primary)' : undefined }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:14, display:'flex', alignItems:'center', gap:8 }}>
                      {p.name}
                      {isOverdue && <span style={{ fontSize:11, color:'var(--color-danger)', fontWeight:700 }}>OVERDUE</span>}
                    </div>
                    <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:3, display:'flex', gap:10, flexWrap:'wrap' }}>
                      <span style={{ fontFamily:'var(--font-mono)' }}>{p.patientUID}</span>
                      {p.phone && <span><User size={10} style={{ verticalAlign:'middle' }}/> {p.phone}</span>}
                      <span style={{ display:'flex', alignItems:'center', gap:4, color:'var(--color-primary)', fontWeight:600 }}>
                        <CalendarDays size={11}/> {p.appointmentDate}
                        {p.appointmentSlot && <><Clock size={11}/> {p.appointmentSlot}</>}
                      </span>
                      {p.doctorName && <span>Dr: {p.doctorName}</span>}
                      {p.reasonForVisit && <span style={{ fontStyle:'italic' }}>{p.reasonForVisit}</span>}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'nowrap' }}>
                    <StatusBadge status={p.status}/>
                    {p.phone && (
                      <WhatsAppButton patient={p} slot={p.appointmentSlot} date={p.appointmentDate} tokenNumber={p.tokenNumber} size="xs"/>
                    )}
                    <button className="btn btn-outline btn-sm" onClick={() => {
                      if (isEditing) { setRescheduleId(null); setNewSlot(''); setNewDate(''); }
                      else { setRescheduleId(p._id); setNewDate(p.appointmentDate||''); setNewSlot(p.appointmentSlot||''); }
                    }}>
                      {isEditing ? 'Cancel' : 'Reschedule'}
                    </button>
                  </div>
                </div>

                {/* Reschedule form */}
                {isEditing && (
                  <div style={{ marginTop:14, padding:'14px 16px', background:'var(--color-surface)', borderRadius:10, border:'1px solid var(--color-border)' }}>
                    <div style={{ fontWeight:700, fontSize:13, marginBottom:10 }}>Set new appointment</div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:12 }}>
                      <div className="field" style={{ margin:0 }}>
                        <label className="label">New Date</label>
                        <input className="input" type="date" value={newDate} onChange={e => setNewDate(e.target.value)} min={todayISO()}/>
                      </div>
                      <div className="field" style={{ margin:0 }}>
                        <label className="label">New Time Slot</label>
                        <input className="input" value={newSlot} onChange={e => setNewSlot(e.target.value)} placeholder="e.g. 10:00 AM–10:30 AM"/>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                      <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => handleReschedule(p)}>
                        {saving ? 'Saving…' : 'Confirm Reschedule'}
                      </button>
                      {p.phone && (
                        <WhatsAppButton
                          patient={{ ...p, appointmentDate: newDate||p.appointmentDate, appointmentSlot: newSlot||p.appointmentSlot }}
                          slot={newSlot||p.appointmentSlot}
                          date={newDate||p.appointmentDate}
                          tokenNumber={p.tokenNumber}
                          size="sm"
                        />
                      )}
                      <span style={{ fontSize:12, color:'var(--color-ink-faint)' }}>Send WhatsApp after confirming</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
